import { isBoolean, isSchemaRecord, isString } from './runtime-values.js';
import type { SchemaInput } from './schema-input.js';

/** A record of origin, not a fresh check of the provider's current boundary. */
export type DrawingGeographicSource = {
  provider: 'openstreetmap';
  featureId: string;
  name: string;
  administrative: boolean;
  geometryFingerprint: string;
  edited?: true;
};
type Point = { lat: number; lng: number };

/** Match the coordinates retained by URL storage; polygon closure is implicit. */
export function geographicGeometryFingerprint(points: Point[]): string {
  const first = points[0], last = points.at(-1);
  const ring = first && last && first.lat === last.lat && first.lng === last.lng ? points.slice(0, -1) : points;
  let hash = 2166136261;
  for (const point of ring) for (const coordinate of [point.lat, point.lng]) hash = Math.imul(hash ^ Math.round(coordinate * 1e5), 16777619);
  return `${ring.length}:${hash >>> 0}`;
}

export function parseDrawingGeographicSource(value: SchemaInput): DrawingGeographicSource | undefined {
  if (!isSchemaRecord(value) || value.provider !== 'openstreetmap' || !isString(value.featureId) || !/^[NWR]\d{1,20}$/u.test(value.featureId)
    || !isString(value.name) || !value.name.trim() || value.name.length > 160 || !isBoolean(value.administrative)
    || !isString(value.geometryFingerprint) || !/^\d{1,6}:\d{1,10}$/u.test(value.geometryFingerprint)) return undefined;
  const source: DrawingGeographicSource = { provider: 'openstreetmap', featureId: value.featureId, name: value.name, administrative: value.administrative, geometryFingerprint: value.geometryFingerprint };
  if (value.edited === true) source.edited = true;
  return source;
}

export function geographicSourceForPoints(value: DrawingGeographicSource | undefined, points: Point[]): DrawingGeographicSource | undefined {
  if (!value) return undefined;
  const source = { ...value };
  if (value.edited || value.geometryFingerprint !== geographicGeometryFingerprint(points)) source.edited = true;
  return source;
}

export function applyDrawingGeographicSource(stroke: { tool: string; points: Point[]; geographicSource?: DrawingGeographicSource }, value: SchemaInput): void {
  if (stroke.tool !== 'polygon') return;
  const source = geographicSourceForPoints(parseDrawingGeographicSource(value), stroke.points);
  if (source) stroke.geographicSource = source;
}
